import React from 'react';

import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { PERMISSION } from 'src/features/auth';
import { identifierTypeTranslationMap } from 'src/features/eventLog/utils/constants';
import { EventData, type EventLog } from 'src/features/eventLog/types';
import { PATH } from 'src/features/navigation/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { cn } from 'src/utils/shadcn';

type CellEventCustomerProps = {
    eventData: EventData;
    event: EventLog;
    onIdentifierClick?: (identifier: string) => void;
};

const CellEventCustomer = ({ eventData, event, onIdentifierClick }: CellEventCustomerProps) => {
    const { formatMessage, locale } = useIntl();
    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);

    const linkClassName = cn('text-primary-700 hover:text-primary-600 cursor-pointer font-semibold');

    // Render the customer identifier as a link to the visitor dashboard. When the visitor id is known
    // (events carrying a visitor object) link straight to it; otherwise resolve the visitor from the
    // identifier value through the parent handler
    const renderValue = (value: string, visitorId?: string): React.ReactNode => {
        // Only link to the visitor dashboard when the user can access visitors.
        if (!accessVisitors) return value;
        if (visitorId) {
            return (
                <Link to={`/${PATH.VISITORS}/visitor/${visitorId}`} className={linkClassName}>
                    {value}
                </Link>
            );
        }
        if (onIdentifierClick) {
            return (
                <button type="button" onClick={() => onIdentifierClick(value)} className={linkClassName}>
                    {value}
                </button>
            );
        }
        return value;
    };

    const getEventCustomerCell = (eventData: EventData, event: EventLog) => {
        if ('identifiers' in eventData && !eventData?.visitor) {
            return (
                <div className="flex flex-col">
                    {eventData?.identifiers?.map((identifier, index) => {
                        return (
                            <div className="flex flex-row gap-1 font-medium" key={index}>
                                <div>{renderValue(identifier.value)}</div>
                                {identifier.type && (
                                    <div className="whitespace-nowrap">{`(${formatMessage(identifierTypeTranslationMap[identifier.type])})`}</div>
                                )}
                            </div>
                        );
                    })}
                    <div>{event?.gateway?.translations?.find((gate) => gate.language === locale)?.name ?? ''}</div>
                </div>
            );
        }
        if ('visitor' in eventData) {
            return (
                <div className="flex flex-col">
                    <div className="flex flex-row gap-1 font-medium">
                        <div>{renderValue(eventData?.visitor?.main_identifier?.value, eventData?.visitor?.id)}</div>
                        {eventData?.visitor?.main_identifier?.type && (
                            <div className="whitespace-nowrap">{`(${formatMessage(identifierTypeTranslationMap[eventData?.visitor?.main_identifier?.type])})`}</div>
                        )}
                    </div>
                    <div>{event?.gateway?.translations?.find((gate) => gate.language === locale)?.name ?? ''}</div>
                </div>
            );
        }
        if ('license_plate' in eventData) {
            return (
                <div className="flex flex-col">
                    <div className="flex flex-row gap-1 font-medium">
                        <div>{renderValue(eventData?.license_plate)}</div>
                        <div className="whitespace-nowrap">
                            {formatMessage(identifierTypeTranslationMap.license_plate)}
                        </div>
                    </div>
                    <div>{event?.gateway?.translations?.find((gate) => gate.language === locale)?.name ?? ''}</div>
                </div>
            );
        }

        return '-';
    };

    return getEventCustomerCell(eventData, event);
};

export default CellEventCustomer;
